import { computed } from 'vue'
import { useRoute } from 'vue-router'
import type { ComputedRef } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

/**
 * The password-reset flow (ForgotPasswordPage.vue / ResetPasswordPage.vue) is
 * shared between organizers and participants. `AuthContext` records which
 * audience entered the flow so "back to login" (and any equivalent
 * organizer-flavoured copy) can return the visitor to the matching login page.
 */
export type AuthContext = 'participant' | 'organizer'

/** Query param carried from every entry point into the shared reset flow. */
export const AUTH_CONTEXT_QUERY_PARAM = 'context'

/**
 * Reads the `context` query param from the current route and resolves the
 * matching login destination. Defaults to 'participant' when the param is
 * absent or unrecognised — this covers the emailed reset link (generated
 * server-side with no context) and any other undetermined case. Every
 * organizer-initiated entry point into the shared reset flow must pass
 * `context=organizer` explicitly to land back on organizer login.
 */
export function useAuthContext(): {
  authContext: ComputedRef<AuthContext>
  loginRoute: ComputedRef<RouteLocationRaw>
} {
  const route = useRoute()

  const authContext = computed<AuthContext>(() => {
    return route.query[AUTH_CONTEXT_QUERY_PARAM] === 'organizer' ? 'organizer' : 'participant'
  })

  const loginRoute = computed<RouteLocationRaw>(() => {
    return authContext.value === 'participant'
      ? { name: 'participant.login' }
      : { name: 'organizer.login' }
  })

  return { authContext, loginRoute }
}
